import { isNil } from 'lodash';
import { Component, Ref } from 'react';
import { KeyValue } from '@mews/enums';
import { isAtMostMobile, withBreakpointRange } from '@optimus-web/breakpoints';
import { ANIMATION_DURATION, InputValue } from '@optimus-web/common';
import { ClickAwayProps, withClickAway, withScrollAway } from '@optimus-web/helpers';
import { IconName } from '@optimus-web/icons';
import { createDateMask } from '@optimus-web/masks';
import { Drawer } from '../Drawer';
import { withFocusTrap } from '../hooks/useFocusTrap';
import { OpenableOverlay } from '../OpenableOverlay';
import { TextInput } from '../TextInput';
import { getBreakpointRangeValueFallback } from '../utils/breakpointsFallback';
import { isDescendant } from '../utils/dom';
import DatePickerComponent from './DatePickerElement';
import { DatePickerContainerElement, DatePickerInputField, DatePickerInputFieldProps } from './style';
import { DatePickerProps, DatePickerState, DatePickerType } from './types';

const MaskedDateField = createDateMask(TextInput);

class DatePickerFieldComponent<D, M, Y> extends Component<DatePickerProps<D, M, Y> & ClickAwayProps, DatePickerState> {
    private isUnmounted?: boolean;
    private containerElement?: HTMLElement;
    private overlayElement?: HTMLElement;
    private drawerElement?: HTMLElement;
    private interactiveIconRef?: HTMLButtonElement;

    static defaultProps = {
        closeOnDateClick: true,
    };

    state = {
        opened: false,
        // support setting the value from date object for legacy code
        value:
            this.props.value ||
            (this.props.dateValue && this.props.getCalendarService().getDateString(this.props.dateValue)) ||
            '',
    };

    UNSAFE_componentWillReceiveProps(nextProps: DatePickerProps<D, M, Y>) {
        // this resets the input when dateValue is set to null
        if (this.props.dateValue != null && nextProps.dateValue == null) {
            this.setState({ value: '' });
        }

        if (nextProps.value !== this.props.value) {
            // shitty angular is shitty angular, it passes nullified values, which would break the component
            const value = isNil(nextProps.value) ? '' : nextProps.value;
            this.setState({ value });
        }
    }

    componentWillUnmount() {
        this.isUnmounted = true;
        this.deregisterBlurHandler();
    }

    handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (!this.state.opened && !this.props.inline && !this.props.readOnly) {
            this.open();
        }

        if (this.props.onFocus && !this.props.readOnly) {
            this.props.onFocus(e);
        }
    };

    handleBlur = () => {
        if (this.isUnmounted) {
            return;
        }

        const roots = [this.containerElement, this.overlayElement, this.drawerElement];
        const shouldClose = !roots.some(
            (root) => document.activeElement && root && isDescendant(document.activeElement, root)
        );

        if (shouldClose) {
            if (this.state.opened) {
                this.close();
            }

            if (this.props.onBlur) {
                this.props.onBlur(undefined as unknown as React.FocusEvent<HTMLInputElement>);
            }
        }
    };

    handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (this.props.keyboardEntryDisabled) {
            e.preventDefault();
            return;
        }

        if (e.key === KeyValue.Enter && this.state.opened) {
            e.stopPropagation();
            e.preventDefault();
            this.close();
        }
    };

    handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        if (this.props.keyboardEntryDisabled) {
            e.preventDefault();
            return;
        }
        this.props.onPaste?.(e);
    };

    handleChange = (e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent, value: InputValue) => {
        const textValue = value.toString();

        this.setState({ value: textValue });

        if (this.props.onChange) {
            this.props.onChange(e, textValue);
        }

        // Provided for legacy code that works with instances rather than with strings
        if (this.props.getCalendarService().isValidDateString(textValue)) {
            const date = this.props.getCalendarService().getDateFromDateString(textValue);
            if (this.props.onDateChange) {
                this.props.onDateChange(date);
            }
        }
    };

    handleDateChange = (date: D) => {
        const { dateFormatPattern, getCalendarService } = this.props;
        const { getDateString, getFormattedDateString } = getCalendarService();
        const value =
            dateFormatPattern && getFormattedDateString
                ? getFormattedDateString(date, dateFormatPattern)
                : getDateString(date);
        this.setState({ value });

        if (this.props.onChange) {
            this.props.onChange(undefined as unknown as React.ChangeEvent<HTMLInputElement>, value);
        }

        if (this.props.onDateChange) {
            this.props.onDateChange(date);
        }
    };

    handleDateClick = (event: React.MouseEvent, date: D) => {
        const isDisabled = this.props.modifiers?.disabled?.(date) ?? false;
        if (this.props.allowDisabledClick && isDisabled) {
            // user clicked to date which is not selectable
            this.props.onDateClick?.(date);
            return;
        }

        if (this.props.closeOnDateClick && this.state.opened) {
            this.close();
            this.handleCloseDrawer();

            if (this.interactiveIconRef) {
                this.interactiveIconRef?.focus();
            }

            if (this.props.onBlur) {
                this.props.onBlur(undefined as unknown as React.FocusEvent<HTMLInputElement>);
            }
        }
    };

    handleDateIconClick = () => {
        if (!this.state.opened) {
            this.open();
        } else if (this.state.opened) {
            this.close();
        }
    };

    registerBlurHandler() {
        document.addEventListener('focus', this.handleBlur, true);
    }

    deregisterBlurHandler() {
        document.removeEventListener('focus', this.handleBlur);
    }

    open() {
        if (!this.state.opened) {
            this.setState({ opened: true });

            this.registerBlurHandler();

            window.setTimeout(() => {
                const options = {
                    handler: this.close,
                    roots: [() => this.containerElement, () => this.overlayElement, () => this.drawerElement],
                };
                this.props.registerClickAway(options);
                this.props.registerScrollAway(options);
                this.props.registerFocusTrap([this.containerElement, this.overlayElement]);
            }, ANIMATION_DURATION.fast);
        }
    }

    close = () => {
        if (this.state.opened) {
            if (!isAtMostMobile(getBreakpointRangeValueFallback(this.props.breakpointRange))) {
                this.setState({ opened: false });
            }
            this.props.deregisterClickAway();
            this.props.deregisterScrollAway();
            this.deregisterBlurHandler();
            this.props.deregisterFocusTrap();
        }
    };

    handleCloseDrawer = () => {
        this.close();
        if (this.state.opened) {
            this.setState({ opened: false });
        }
    };

    containerRef = (c: HTMLDivElement) => {
        this.containerElement = c;
        if (this.props.innerRef) {
            this.props.innerRef(c);
        }
    };

    interactiveIconRefCallback = (c: HTMLButtonElement | null) => {
        this.interactiveIconRef = c || undefined;
    };

    handlerOverlayRef = (c: HTMLDivElement) => (this.overlayElement = c);

    anchorElementGetter = () => this.containerElement;

    handleDialogClose = () => this.close();

    render() {
        const {
            getCalendarService,
            keyboardEntryDisabled,
            monthSelectorVisible,
            modifiers,
            innerRef,
            minDate,
            maxDate,
            dateFormatPattern,
            drawerCloseHandler,
            name,
            inline,

            // omitted props
            allowDisabledClick,
            numberOfMonths,
            month,
            getDateTooltip,
            onBlur,
            onDateChange,
            tooltipDelay,
            enableAdjacentMonthSelection,
            ...other
        } = this.props;
        const { opened, value } = this.state;
        let activeDate: D | undefined;

        if (getCalendarService().isValidDateString(value.toString())) {
            activeDate = getCalendarService().getDateFromDateString(value.toString());
        }

        const datePickerContainerProps = {
            ref: this.containerRef,
        };

        const calendarProps = {
            date: activeDate,
            getCalendarService,
            modifiers,
            dataTestName: name,
            onDateClick: this.handleDateClick,
            onDateChange: this.handleDateChange,
            monthSelectorVisible,
            minDate,
            maxDate,
            month,
            allowDisabledClick,
            getDateTooltip,
            numberOfMonths,
            tooltipDelay,
            enableAdjacentMonthSelection,
        };

        const inputProps = {
            ...other,
            keyboardEntryDisabled,
            name,
            value: this.state.value as string,
            dateValue: this.state.value as string,
            onFocus: this.handleFocus,
            onChange: this.handleChange,
            onKeyDown: this.handleKeyDown,
            onPaste: this.handlePaste,
            inputMode: keyboardEntryDisabled ? 'none' : other.inputMode,
            interactiveIcon: inline ? undefined : IconName.Calendar,
            interactiveIconHandler: this.handleDateIconClick,
            interactiveIconRef: this.interactiveIconRefCallback,
            interactiveIconExtraProps: {
                'aria-expanded': opened,
            },
            inline: false,
            onBlur: inline ? this.handleBlur : undefined,
            'aria-expanded': opened,
        };

        const overlayProps = {
            anchorElement: this.anchorElementGetter,
            opened,
            innerRef: this.handlerOverlayRef,
        };

        const dayPickerComponent = () => {
            if (isAtMostMobile(getBreakpointRangeValueFallback(this.props.breakpointRange))) {
                return (
                    <Drawer
                        closeHandler={drawerCloseHandler}
                        open={opened}
                        onClose={this.handleCloseDrawer}
                        // @ts-expect-error
                        ref={(el) => (this.drawerElement = el)}
                    >
                        <DatePickerComponent {...calendarProps} />
                    </Drawer>
                );
            }
            return (
                <OpenableOverlay {...overlayProps}>
                    <DatePickerComponent {...calendarProps} />
                </OpenableOverlay>
            );
        };

        return (
            <DatePickerContainerElement {...datePickerContainerProps}>
                {dateFormatPattern ? (
                    <MaskedDateField {...inputProps} dateFormatPattern={dateFormatPattern} />
                ) : (
                    <DatePickerInputField
                        ref={this.props.inputRef as Ref<Component<DatePickerInputFieldProps>> | undefined}
                        {...inputProps}
                    />
                )}
                {!inline ? dayPickerComponent() : null}
            </DatePickerContainerElement>
        );
    }
}

// @WORKAROUND - to handle HOC and generics variables types, in the typescript
export const DatePicker = withBreakpointRange(
    withFocusTrap(withScrollAway(withClickAway(DatePickerFieldComponent)))
) as unknown as DatePickerType;
